import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Card, CardContent, Grid2 as Grid,
  Chip, Alert, CircularProgress, Tab, Tabs,
  Table, TableBody, TableCell, TableHead, TableRow, Paper,
  Avatar, Tooltip, IconButton, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, MenuItem, Snackbar, InputAdornment,
} from '@mui/material';
import {
  ArrowBack, Refresh, People, ShoppingCart, Storefront, Business,
  CheckCircle, Cancel, PersonAdd, Visibility, VisibilityOff,
} from '@mui/icons-material';
import numishubService, {
  NumisHubTenant, NumisHubTenantStats, NumisHubCoin, NumisHubOrder,
  NumisHubUser, CreateTenantUserInput,
} from '../../services/numishub.service';

const STATUS_LABEL: Record<string, string> = {
  available: 'Disponível', sold_out: 'Esgotado', upcoming: 'Em breve', discontinued: 'Descontinuado',
};
const STATUS_COLOR: Record<string, 'success' | 'error' | 'warning' | 'default'> = {
  available: 'success', sold_out: 'error', upcoming: 'warning', discontinued: 'default',
};
const ORDER_COLOR: Record<string, 'default' | 'warning' | 'success' | 'info' | 'error'> = {
  pending: 'warning', paid: 'success', processing: 'info',
  shipped: 'info', delivered: 'success', cancelled: 'error', refunded: 'default',
};
const EMPTY_USER_FORM: CreateTenantUserInput = { name: '', email: '', password: '', role: 'bank_admin' };

export default function NumisHubTenantPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();

  const [tenant, setTenant]       = useState<NumisHubTenant | null>(null);
  const [stats, setStats]         = useState<NumisHubTenantStats | null>(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState<string | null>(null);
  const [tab, setTab]             = useState(0);
  const [successMsg, setSuccessMsg] = useState('');

  const [coins, setCoins]         = useState<NumisHubCoin[]>([]);
  const [coinsLoading, setCoinsLoading] = useState(false);

  const [orders, setOrders]       = useState<NumisHubOrder[]>([]);
  const [ordersLoading, setOrdersLoading] = useState(false);

  const [users, setUsers]         = useState<NumisHubUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [userForm, setUserForm]   = useState<CreateTenantUserInput>(EMPTY_USER_FORM);
  const [creatingUser, setCreatingUser] = useState(false);
  const [userFormError, setUserFormError] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const loadMain = useCallback(async () => {
    if (!slug) return;
    setLoading(true); setError(null);
    try {
      const { tenant: t, stats: s } = await numishubService.getTenant(slug);
      setTenant(t); setStats(s);
    } catch {
      setError('Não foi possível carregar dados do banco.');
    } finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { loadMain(); }, [loadMain]);

  const loadCoins = useCallback(async () => {
    if (!slug) return;
    setCoinsLoading(true);
    try { setCoins(await numishubService.listTenantCoins(slug)); }
    catch { setCoins([]); }
    finally { setCoinsLoading(false); }
  }, [slug]);

  const loadOrders = useCallback(async () => {
    if (!slug) return;
    setOrdersLoading(true);
    try { setOrders(await numishubService.listTenantOrders(slug)); }
    catch { setOrders([]); }
    finally { setOrdersLoading(false); }
  }, [slug]);

  const loadUsers = useCallback(async () => {
    if (!slug) return;
    setUsersLoading(true);
    try { setUsers(await numishubService.listTenantUsers(slug)); }
    catch { setUsers([]); }
    finally { setUsersLoading(false); }
  }, [slug]);

  const handleTabChange = (_: React.SyntheticEvent, v: number) => {
    setTab(v);
    if (v === 1 && !coins.length)  loadCoins();
    if (v === 2 && !orders.length) loadOrders();
    if (v === 3 && !users.length)  loadUsers();
  };

  const refreshTab = () => {
    if (tab === 0) loadMain();
    if (tab === 1) loadCoins();
    if (tab === 2) loadOrders();
    if (tab === 3) loadUsers();
  };

  const handleCreateUser = async () => {
    if (!userForm.name || !userForm.email || !userForm.password) {
      setUserFormError('Preencha todos os campos obrigatórios.'); return;
    }
    if (!slug) return;
    setCreatingUser(true); setUserFormError('');
    try {
      await numishubService.createTenantUser(slug, userForm);
      setUserDialogOpen(false);
      setUserForm(EMPTY_USER_FORM);
      setSuccessMsg(`Utilizador '${userForm.email}' criado com sucesso.`);
      loadUsers();
    } catch (err: any) {
      setUserFormError(err.response?.data?.error ?? err.message ?? 'Erro ao criar utilizador');
    } finally { setCreatingUser(false); }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !tenant) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" action={
          <Button size="small" onClick={() => navigate('/numishub')}>Voltar</Button>
        }>{error ?? 'Banco não encontrado'}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Cabeçalho */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/numishub')} size="small">
          <ArrowBack />
        </IconButton>
        <Avatar sx={{ bgcolor: 'primary.main', width: 44, height: 44, fontWeight: 'bold' }}>
          {tenant.name[0]}
        </Avatar>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="bold" lineHeight={1.2}>{tenant.name}</Typography>
          <Typography variant="caption" color="text.secondary">
            {tenant.slug} · {tenant.country_name}
          </Typography>
        </Box>
        <Chip label={tenant.plan} color={tenant.plan === 'enterprise' ? 'success' : tenant.plan === 'pro' ? 'primary' : 'default'} />
        <Chip label={tenant.active ? 'Activo' : 'Inactivo'} color={tenant.active ? 'success' : 'default'} />
        <Tooltip title="Actualizar">
          <IconButton size="small" onClick={refreshTab}><Refresh /></IconButton>
        </Tooltip>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tab} onChange={handleTabChange} sx={{ px: 2 }}>
          <Tab label="Visão Geral" />
          <Tab label="Catálogo" icon={<Storefront fontSize="small" />} iconPosition="start" />
          <Tab label="Encomendas" icon={<ShoppingCart fontSize="small" />} iconPosition="start" />
          <Tab label="Utilizadores" icon={<People fontSize="small" />} iconPosition="start" />
        </Tabs>
      </Paper>

      {/* Tab 0 — Visão Geral */}
      {tab === 0 && (
        <Grid container spacing={3}>
          {/* Stats */}
          {stats && (
            <>
              {[
                { icon: <Storefront color="primary" />, label: 'Peças', value: stats.coins?.total ?? 0, sub: `${stats.coins?.stock_total ?? 0} em stock` },
                { icon: <People color="secondary" />, label: 'Colecionadores', value: stats.users?.total ?? 0, sub: `${stats.users?.active ?? 0} activos` },
                { icon: <ShoppingCart color="success" />, label: 'Encomendas', value: stats.orders?.total ?? 0, sub: `${stats.orders?.paid ?? 0} pagas` },
                { icon: <Business color="info" />, label: 'Receita', value: `€${Number(stats.orders?.revenue ?? 0).toFixed(2)}`, sub: '' },
              ].map(s => (
                <Grid key={s.label} size={{ xs: 6, md: 3 }}>
                  <Card>
                    <CardContent sx={{ p: 2.5, '&:last-child': { pb: 2.5 } }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <Box>
                          <Typography variant="h4" fontWeight="bold" lineHeight={1}>{s.value}</Typography>
                          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{s.label}</Typography>
                          {s.sub && <Typography variant="caption" color="text.disabled">{s.sub}</Typography>}
                        </Box>
                        {s.icon}
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </>
          )}

          {/* Info */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Informação</Typography>
                {[
                  ['País', `${tenant.country_name} (${tenant.country})`],
                  ['Base de dados', `numishub_${tenant.slug}`],
                  ['Domínio', tenant.domain ?? '—'],
                  ['Plano', tenant.plan],
                  ['Criado em', new Date(tenant.created_at).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })],
                ].map(([k, v]) => (
                  <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.75, borderBottom: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body2" color="text.secondary">{k}</Typography>
                    <Typography variant="body2" fontWeight="medium">{v}</Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Grid>

          {/* Acções rápidas */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Card>
              <CardContent>
                <Typography variant="subtitle1" fontWeight="bold" gutterBottom>Acções rápidas</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
                  <Button variant="outlined" startIcon={<Storefront />} onClick={() => { setTab(1); loadCoins(); }}>
                    Ver catálogo de moedas
                  </Button>
                  <Button variant="outlined" startIcon={<People />} onClick={() => { setTab(3); loadUsers(); }}>
                    Gerir utilizadores
                  </Button>
                  <Button variant="outlined" startIcon={<ShoppingCart />} onClick={() => { setTab(2); loadOrders(); }}>
                    Ver encomendas
                  </Button>
                  {tenant.domain && (
                    <Button variant="outlined" href={`https://${tenant.domain}`} target="_blank" rel="noopener">
                      Abrir portal público ↗
                    </Button>
                  )}
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Tab 1 — Catálogo */}
      {tab === 1 && (
        coinsLoading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : coins.length === 0 ? (
          <Alert severity="info">Sem moedas no catálogo.</Alert>
        ) : (
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Peça</TableCell>
                  <TableCell>Série</TableCell>
                  <TableCell>Ano</TableCell>
                  <TableCell>Metal</TableCell>
                  <TableCell align="right">Preço</TableCell>
                  <TableCell align="right">Stock</TableCell>
                  <TableCell align="right">Tiragem</TableCell>
                  <TableCell>Estado</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {coins.map(c => (
                  <TableRow key={c.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        {c.image ? (
                          <Box component="img" src={c.image} alt={c.title}
                            sx={{ width: 36, height: 36, borderRadius: 1, objectFit: 'cover', border: '1px solid', borderColor: 'divider' }} />
                        ) : (
                          <Box sx={{ width: 36, height: 36, borderRadius: 1, bgcolor: 'action.hover', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Storefront sx={{ fontSize: 18, color: 'text.disabled' }} />
                          </Box>
                        )}
                        <Box>
                          <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.2 }}>{c.title}</Typography>
                          <Typography variant="caption" color="text.secondary">{c.slug}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell><Typography variant="caption">{c.series ?? '—'}</Typography></TableCell>
                    <TableCell>{c.year}</TableCell>
                    <TableCell><Typography variant="caption">{c.metal}</Typography></TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {Number(c.price).toFixed(2)} {c.currency}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">{c.stock}</TableCell>
                    <TableCell align="right">{c.mintage ?? '—'}</TableCell>
                    <TableCell>
                      <Chip label={STATUS_LABEL[c.status] ?? c.status} color={STATUS_COLOR[c.status] ?? 'default'} size="small" />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )
      )}

      {/* Tab 2 — Encomendas */}
      {tab === 2 && (
        ordersLoading ? (
          <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
        ) : orders.length === 0 ? (
          <Alert severity="info">Sem encomendas registadas.</Alert>
        ) : (
          <Paper variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Referência</TableCell>
                  <TableCell>Colecionador</TableCell>
                  <TableCell>Peças</TableCell>
                  <TableCell align="right">Total</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Data</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.map(o => (
                  <TableRow key={o.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontFamily="monospace">{o.reference}</Typography>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">{o.user_name}</Typography>
                        <Typography variant="caption" color="text.secondary">{o.user_email}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">{o.item_count}</TableCell>
                    <TableCell align="right">
                      <Typography variant="body2" fontWeight="medium">
                        {Number(o.total_amount).toFixed(2)} {o.currency}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={o.status} color={ORDER_COLOR[o.status] ?? 'default'} size="small" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {new Date(o.created_at).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        )
      )}

      {/* Tab 3 — Utilizadores */}
      {tab === 3 && (
        <Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button variant="contained" size="small" startIcon={<PersonAdd />}
              onClick={() => { setUserForm(EMPTY_USER_FORM); setUserFormError(''); setUserDialogOpen(true); }}>
              Criar Utilizador
            </Button>
          </Box>
          {usersLoading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>
          ) : users.length === 0 ? (
            <Alert severity="info">Nenhum utilizador encontrado.</Alert>
          ) : (
            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Papel</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell>Criado em</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map(u => (
                    <TableRow key={u.id} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: 12, bgcolor: 'primary.light' }}>
                            {u.name[0]}
                          </Avatar>
                          {u.name}
                        </Box>
                      </TableCell>
                      <TableCell>{u.email}</TableCell>
                      <TableCell>
                        <Chip label={u.role === 'bank_admin' ? 'Admin' : u.role === 'bank_staff' ? 'Staff' : 'Colecionador'}
                          color={u.role === 'bank_admin' ? 'primary' : u.role === 'bank_staff' ? 'secondary' : 'default'}
                          size="small" />
                      </TableCell>
                      <TableCell>
                        <Chip label={u.active ? 'Activo' : 'Inactivo'} color={u.active ? 'success' : 'default'} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(u.created_at).toLocaleDateString('pt-PT', { timeZone: 'Atlantic/Cape_Verde' })}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
          )}
        </Box>
      )}

      {/* Dialog: Criar Utilizador */}
      <Dialog open={userDialogOpen} onClose={() => setUserDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Criar Utilizador — {tenant.name}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            {userFormError && <Alert severity="error">{userFormError}</Alert>}
            <TextField label="Nome completo *" value={userForm.name}
              onChange={e => setUserForm(f => ({ ...f, name: e.target.value }))} fullWidth />
            <TextField label="Email *" type="email" value={userForm.email}
              onChange={e => setUserForm(f => ({ ...f, email: e.target.value }))} fullWidth />
            <TextField
              label="Password *"
              type={showPassword ? 'text' : 'password'}
              value={userForm.password}
              onChange={e => setUserForm(f => ({ ...f, password: e.target.value }))}
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(v => !v)} edge="end" size="small">
                      {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField select label="Papel" value={userForm.role}
              onChange={e => setUserForm(f => ({ ...f, role: e.target.value as any }))} fullWidth>
              <MenuItem value="bank_admin">Admin do banco</MenuItem>
              <MenuItem value="bank_staff">Staff do banco</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUserDialogOpen(false)}>Cancelar</Button>
          <Button variant="contained" onClick={handleCreateUser} disabled={creatingUser}
            startIcon={creatingUser ? <CircularProgress size={16} /> : <PersonAdd />}>
            {creatingUser ? 'A criar...' : 'Criar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!successMsg} autoHideDuration={4000} onClose={() => setSuccessMsg('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert severity="success" onClose={() => setSuccessMsg('')}>{successMsg}</Alert>
      </Snackbar>
    </Box>
  );
}
